import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../lib/api";
import type { BrandEdit, BrandEditSourceType, TrustFeedbackTag } from "../../../shared/types";

export function useBrandCorrections(
  brandId: string | undefined,
  opts?: { active?: boolean; sourceType?: BrandEditSourceType; enabled?: boolean },
) {
  return useQuery({
    queryKey: ["brand-corrections", brandId, opts?.active ?? true, opts?.sourceType ?? "all"],
    queryFn: () => {
      const params = new URLSearchParams();
      if (opts?.active === false) params.set("active", "false");
      if (opts?.sourceType) params.set("source_type", opts.sourceType);
      const qs = params.toString();
      return api.get<{ data: BrandEdit[] }>(`/api/brands/${brandId}/corrections${qs ? `?${qs}` : ""}`);
    },
    enabled: !!brandId && (opts?.enabled ?? true),
  });
}

export function useCreateCorrection(brandId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: {
      source_type: BrandEditSourceType;
      source_id: string | null;
      correction_text: string;
      trust_tags?: TrustFeedbackTag[];
    }) => api.post<{ data: BrandEdit }>(`/api/brands/${brandId}/corrections`, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["brand-corrections", brandId] }),
  });
}

export function useUpdateCorrection(brandId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: { correctionId: string; correction_text?: string; active?: boolean; trust_tags?: TrustFeedbackTag[] }) =>
      api.patch<{ data: BrandEdit }>(`/api/brands/${brandId}/corrections/${data.correctionId}`, {
        correction_text: data.correction_text,
        active: data.active,
        trust_tags: data.trust_tags,
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["brand-corrections", brandId] }),
  });
}

export function useArchiveCorrection(brandId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (correctionId: string) =>
      api.patch<{ data: BrandEdit }>(`/api/brands/${brandId}/corrections/${correctionId}`, {
        active: false,
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["brand-corrections", brandId] }),
  });
}
